import React from 'react';
import * as T from '../time.js';
import { api } from '../api.js';
import { Empty, Lbl, Loading, PH, Panel } from './components.jsx';
import { ME, PEOPLE } from './data.jsx';
import { Button, DataTable, Pill, SegmentedControl } from './ds/fgcp-ds.jsx';

// approval-delegations.jsx — 대리결재 위임(계획 문서 5.2 2-1d). 무엇을 할 수 있는지(다른 사람 위임·취소)는 서버가 준 actions로만 정한다.

const { useState: uS, useEffect: uE } = React;

const STATUS = { scheduled: ['예정', 'info'], active: ['진행 중', 'ok'], ended: ['끝남', 'neutral'], cancelled: ['취소', 'neutral'] };
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5 };
const today = () => T.todayYmd();   // 기본값용(앱 시간대의 오늘) — 판단은 서버(DB 날짜)가 한다

/** 위임 만들기 — 대리인·기간·사유. 결재 관리자는 위임하는 사람도 고른다(갑작스러운 부재) */
function NewDelegation({ manageOthers, onSaved, onToast }) {
  const [delegatorId, setDelegatorId] = uS(ME.id);
  const [delegateId, setDelegateId] = uS('');
  const [startsOn, setStartsOn] = uS(today());
  const [endsOn, setEndsOn] = uS(today());
  const [reason, setReason] = uS('');
  const [busy, setBusy] = uS(false);
  const people = [...PEOPLE].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  const save = () => {
    setBusy(true);
    api.post('/api/v1/approvals/delegations', {
      ...(manageOthers && delegatorId !== ME.id ? { delegatorId } : {}), delegateId, startsOn, endsOn, reason: reason.trim() || null
    })
      .then(() => { onToast('위임을 만들었습니다 — 대리인에게 알렸습니다'); setDelegateId(''); setReason(''); onSaved(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <div style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', borderBottom: '1px solid var(--line)' }} data-delegation-form>
      {manageOthers && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Lbl>위임하는 사람</Lbl>
          <select style={box} value={delegatorId} onChange={e => setDelegatorId(e.target.value)} aria-label="위임하는 사람">
            {people.map(p => <option key={p.id} value={p.id}>{p.name}{p.dept ? ' · ' + p.dept : ''}</option>)}
          </select>
        </label>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Lbl>대리인</Lbl>
        <select style={box} value={delegateId} onChange={e => setDelegateId(e.target.value)} aria-label="대리인">
          <option value="">— 고르세요 —</option>
          {people.filter(p => p.id !== delegatorId).map(p => <option key={p.id} value={p.id}>{p.name}{p.dept ? ' · ' + p.dept : ''}</option>)}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Lbl>기간</Lbl>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="date" style={box} value={startsOn} onChange={e => setStartsOn(e.target.value)} aria-label="위임 시작일" />
          <span style={{ color: 'var(--ink-3)' }}>~</span>
          <input type="date" style={box} value={endsOn} onChange={e => setEndsOn(e.target.value)} aria-label="위임 끝일" />
        </span>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px' }}>
        <Lbl>사유 (선택)</Lbl>
        <input style={box} value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="예: 해외 출장" aria-label="위임 사유" />
      </label>
      <Button variant="primary" size="sm" disabled={busy || !delegateId || !startsOn || !endsOn} onClick={save}>위임하기</Button>
    </div>
  );
}

/** 위임 목록 — 내가 맡긴 것·나에게 맡겨진 것(결재 관리자는 전체도). 취소는 서버가 준 actions.cancel */
function DelegationManager({ onToast }) {
  const [scope, setScope] = uS('mine');
  const [data, setData] = uS(null);
  const [rev, setRev] = uS(0);
  uE(() => {
    let current = true;
    api.get('/api/v1/approvals/delegations?scope=' + scope)
      .then(r => { if (current) setData(r); })
      .catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [scope, rev, onToast]);
  const reload = () => setRev(r => r + 1);
  const cancel = d => api.post('/api/v1/approvals/delegations/' + d.id + '/cancel', {})
    .then(() => { onToast('위임을 취소했습니다'); reload(); })
    .catch(e => onToast(e.message));
  const manageOthers = data?.actions?.manageOthers === true;
  return (
    <Panel scroll>
      <PH title="대리결재 위임" sub="위임 기간에는 대리인도 내 결재·합의 차례를 처리합니다(나도 그대로 처리 가능)"
        right={manageOthers && (
          <SegmentedControl size="sm" value={scope} onChange={setScope} options={[{ value: 'mine', label: '내 위임' }, { value: 'all', label: '전체' }]} />
        )} />
      <NewDelegation manageOthers={manageOthers} onSaved={reload} onToast={onToast} />
      {!data ? <Loading title="불러오는 중" /> : data.delegations.length === 0 ? <Empty title="위임이 없습니다" sub="부재 기간에 결재를 맡길 사람을 정하세요" /> : (
        <DataTable columns={[
          { key: 'delegator', header: '위임하는 사람', render: d => <span style={{ fontWeight: d.delegator_user_id === ME.id ? 700 : 400 }}>{d.delegator_name}</span> },
          { key: 'delegate', header: '대리인', render: d => <span style={{ fontWeight: d.delegate_user_id === ME.id ? 700 : 400 }}>{d.delegate_name}</span> },
          { key: 'period', header: '기간', mono: true, render: d => `${d.starts_on} ~ ${d.ends_on}` },
          { key: 'reason', header: '사유', render: d => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{d.reason || '—'}</span> },
          { key: 'status', header: '상태', width: 80, render: d => { const [label, tone] = STATUS[d.status] ?? [d.status, 'neutral']; return <Pill tone={tone} dot={false}>{label}</Pill>; } },
          { key: 'act', header: '', width: 70, render: d => d.actions.cancel
            ? <Button variant="secondary" size="sm" onClick={() => cancel(d)} aria-label={`${d.delegate_name} 위임 취소`}>취소</Button> : null },
        ]} rows={data.delegations.map(d => ({ ...d, id: d.id }))} />
      )}
    </Panel>
  );
}

export { DelegationManager };
